import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';

import { type Repub } from '@cx-modules/constants';

import {
	type BettingInsightsResultAttribute,
	type EsIndex,
} from '~/configuration/betting-insights/types';
import { useSearchWithAutocomplete } from '~/hooks/useSearchWithAutocomplete';

import { AutocompleteDropdown } from './AutocompleteDropdown';

export function Autocomplete<TIndex extends EsIndex>({
	disabled = false,
	indexName,
	repub,
	searchAttributes,
}: {
	disabled?: boolean;
	indexName: TIndex;
	repub: Repub;
	searchAttributes: BettingInsightsResultAttribute<TIndex>[];
}) {
	const [inputValue, setInputValue] = useState('');
	const [inputIsFocused, setInputIsFocused] = useState(false);
	const [isDropdownVisible, setIsDropdownVisible] = useState(false);
	const [selectedTagIndex, setSelectedTagIndex] = useState(-1);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const {
		activeIndex,
		getInputProps,
		handleClearSearchResults,
		handleKeyDown,
		searchResults,
		selectedTags,
		setActiveIndex,
		setSelectedTags,
	} = useSearchWithAutocomplete({
		inputRef,
		indexName,
		repub,
		searchAttributes,
		setInputValue,
		setIsDropdownVisible,
	});

	const handleBlur = useCallback(() => {
		setInputIsFocused(false);
	}, []);

	const handleFocus = useCallback(() => {
		setInputIsFocused(true);
		setInputValue(value => value);
	}, []);

	const handleClickOutside = useCallback(
		(e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setIsDropdownVisible(false);
			}
		},
		[setIsDropdownVisible],
	);

	const handleCloseDropdown = useCallback(() => {
		setIsDropdownVisible(false);
	}, [setIsDropdownVisible]);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [handleClickOutside]);

	const handleTagSelection = useCallback(
		(tag: string) => {
			if (!selectedTags.includes(tag)) {
				setSelectedTags(tags => [...tags, tag]);
				setIsDropdownVisible(false);
				setActiveIndex(-1);
				setInputValue('');
				setSelectedTagIndex(-1);
				inputRef.current?.focus();
			}
			handleClearSearchResults();
		},
		[handleClearSearchResults, selectedTags, setActiveIndex, setSelectedTags],
	);

	const handleTagRemoval = useCallback(
		(tagToRemove: string, index?: number) => {
			setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
			if (typeof index === 'number') {
				setSelectedTagIndex(Math.max(-1, index - 1));
			}
		},
		[setSelectedTags, selectedTags],
	);

	const handleInputKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'ArrowLeft') {
				if (selectedTagIndex >= 0) {
					e.preventDefault();
					setSelectedTagIndex(index => Math.max(0, index - 1));
				} else if (inputRef.current?.selectionStart === 0) {
					e.preventDefault();
					setSelectedTagIndex(selectedTags.length - 1);
				}
				return;
			}

			if (e.key === 'ArrowRight') {
				if (selectedTagIndex >= 0) {
					e.preventDefault();
					setSelectedTagIndex(index =>
						index < selectedTags.length - 1 ? index + 1 : -1,
					);
				}
				return;
			}

			if (e.key === 'Backspace') {
				if (selectedTagIndex >= 0) {
					e.preventDefault();
					handleTagRemoval(selectedTags[selectedTagIndex], selectedTagIndex);
				} else if (inputValue.length === 0 && selectedTags.length > 0) {
					const lastTag = selectedTags[selectedTags.length - 1];
					handleTagRemoval(lastTag);
				}
				return;
			}

			handleKeyDown(e, () => {
				if (activeIndex !== -1 && searchResults[activeIndex]) {
					handleTagSelection(searchResults[activeIndex]);
				} else if (searchResults.length === 1) {
					handleTagSelection(searchResults[0]);
				}
			});
		},
		[
			activeIndex,
			handleKeyDown,
			handleTagRemoval,
			handleTagSelection,
			inputValue.length,
			searchResults,
			selectedTags,
			selectedTagIndex,
		],
	);

	const handleClearSearch = useCallback(() => {
		setInputValue('');
		setSelectedTags([]);
		setSelectedTagIndex(-1);
		handleClearSearchResults();
	}, [handleClearSearchResults, setInputValue, setSelectedTags]);

	return (
		<div className="relative w-full mb-6" ref={containerRef}>
			<div className="flex items-center border border-neutral-200 border-gray-600 rounded-md bg-[#3C5668] overflow-hidden dark:border-neutral-800">
				<div className="px-3 py-2">
					<Search
						className={
							disabled
								? 'text-gray'
								: inputIsFocused
									? 'text-[#00E889]'
									: 'text-white'
						}
						size={20}
					/>
				</div>
				<div className="flex-grow flex items-center flex-wrap gap-1 py-1 pl-0 pr-2">
					{selectedTags.map((tag, index) => (
						<span
							className={`flex items-center justify-center ${
								selectedTagIndex === index
									? 'bg-[#00B36D] ring-2 ring-[#00E889]'
									: 'bg-[#00E889]'
							} text-[#00182E] rounded-full px-2 py-1 text-sm`}
							key={tag}
						>
							{tag}
							<button
								className="ml-1 text-[#00182E]"
								onClick={() => handleTagRemoval(tag)}
							>
								<X size={14} />
							</button>
						</span>
					))}
					<input
						{...getInputProps()}
						className={`flex-grow outline-none pr-2 py-1 bg-transparent text-white placeholder-white text-md ${
							selectedTagIndex >= 0 ? 'caret-transparent' : ''
						}`}
						disabled={disabled}
						onBlur={handleBlur}
						onFocus={() => {
							handleFocus();
							setSelectedTagIndex(-1);
						}}
						onKeyDown={handleInputKeyDown}
						placeholder={
							selectedTags.length === 0 ? 'Search team, player or bet type' : ''
						}
						ref={inputRef}
						type="text"
						value={inputValue}
					/>
				</div>
				{selectedTags.length > 0 || inputValue.length > 0 ? (
					<div className="flex items-center px-3 py-2">
						<button className="ml-1 text-[#00182E]" onClick={handleClearSearch}>
							<X className="text-white" size={20} />
						</button>
					</div>
				) : null}
			</div>
			{isDropdownVisible && searchResults.length > 0 && (
				<AutocompleteDropdown
					activeIndex={activeIndex}
					handleCloseDropdown={handleCloseDropdown}
					onSelect={handleTagSelection}
					results={searchResults}
				/>
			)}
		</div>
	);
}
